import { countryConfig } from '../country';
import { LocaleLink as Link } from '../i18n/locale-link';
import { PressBlock } from '../components/press-block';
import { Seo } from '../components/seo';
import { useLocale, useT } from '../i18n/locale-context';
import { COMPANY, HAS_SUPPORT_TELEGRAM } from '../lib/company';

/** /about — public "about the service" page. Static prose, SSR-prerendered, i18n-driven.
 *  Doubles as an SEO surface (госзакупки Узбекистана / uztender keywords). */
export function About() {
  const t = useT();
  const { locale } = useLocale();

  const features = [
    { title: t('about.feature.catalog.title'), desc: t('about.feature.catalog.desc') },
    { title: t('about.feature.ai.title'), desc: t('about.feature.ai.desc') },
    { title: t('about.feature.search.title'), desc: t('about.feature.search.desc') },
    { title: t('about.feature.alerts.title'), desc: t('about.feature.alerts.desc') },
  ];

  return (
    <div className="mx-auto max-w-[820px]">
      <Seo
        title={t('about.pageTitle')}
        description={t('about.subtitle')}
        canonical="/about"
        lang={locale}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: t('about.pageTitle'),
          url: `${countryConfig.brand.siteUrl}/about`,
          publisher: {
            '@type': 'Organization',
            name: countryConfig.brand.name,
            url: countryConfig.brand.siteUrl,
            legalName: COMPANY.legalName,
          },
        }}
      />

      <h1 className="mb-1 font-brand text-[24px] font-extrabold tracking-[-0.3px] text-ink">
        {t('about.pageTitle')}
      </h1>
      <p className="mb-6 text-[14px] text-ink-soft">{t('about.subtitle')}</p>

      <p className="mb-8 text-[15px] leading-[1.7] text-ink">{t('about.lead')}</p>

      {/* What we do */}
      <h2 className="mb-2 font-brand text-[17px] font-bold text-ink">{t('about.what.title')}</h2>
      <p className="mb-7 text-[14px] leading-[1.7] text-ink-soft">{t('about.what.body')}</p>

      {/* Feature grid */}
      <div className="mb-8 grid gap-3 sm:grid-cols-2">
        {features.map((f) => (
          <div key={f.title} className="rounded-lg border border-edge bg-bone-surface p-4">
            <div className="mb-1 text-[14px] font-bold text-ink">{f.title}</div>
            <div className="text-[13px] leading-[1.6] text-ink-muted">{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Audience */}
      <h2 className="mb-2 font-brand text-[17px] font-bold text-ink">
        {t('about.audience.title')}
      </h2>
      <p className="mb-7 text-[14px] leading-[1.7] text-ink-soft">{t('about.audience.body')}</p>

      {/* Data sources */}
      <h2 className="mb-2 font-brand text-[17px] font-bold text-ink">{t('about.sources.title')}</h2>
      <p className="mb-7 text-[14px] leading-[1.7] text-ink-soft">{t('about.sources.body')}</p>

      {/* Press — third-party coverage. Renders nothing when there is none. */}
      <PressBlock />

      {/* Contact */}
      <h2 className="mb-2 font-brand text-[17px] font-bold text-ink">{t('about.contact.title')}</h2>
      <p className="mb-2 text-[14px] leading-[1.7] text-ink-soft">{t('about.contact.body')}</p>
      <p className="mb-8 text-[14px] text-ink-soft">
        <a href={COMPANY.emailUrl} className="font-medium text-brand hover:underline">
          {COMPANY.email}
        </a>
        {HAS_SUPPORT_TELEGRAM && (
          <>
            <span className="mx-2 text-ink-muted">·</span>
            <a
              href={COMPANY.supportTelegramUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand hover:underline"
            >
              {COMPANY.supportTelegram}
            </a>
          </>
        )}
      </p>

      {/* Operator disclosure */}
      <p className="border-t border-edge-light pt-5 text-[12.5px] leading-[1.6] text-ink-muted">
        {t('about.operator', { legalName: COMPANY.legalName })}{' '}
        <Link to="/requisites" className="font-medium text-brand hover:underline">
          {t('about.operator.requisitesLink')}
        </Link>
        .
      </p>
    </div>
  );
}
